import { type OfferPageHighlightTagContext } from './useOfferPageHighlightTags'

function isTruthyString(value: string | undefined): value is string {
  return !!value?.trim()
}

function getPropertyRoomSizeSqm(size: string | undefined) {
  const sqmMatch = size?.match(/(\d+(?:\.\d+)?)\s*(?:m²|m2|sqm|sq m)/i)
  if (sqmMatch?.[1]) return Number(sqmMatch[1])

  const sqftMatch = size?.match(/(\d+(?:\.\d+)?)\s*(?:ft²|ft2|sqft|sq ft)/i)
  if (sqftMatch?.[1]) return Number(sqftMatch[1]) * 0.092903

  return undefined
}

function getBedbankRoomFeatureText(pkg: App.BedbankPackage, featureId: string) {
  return pkg.features.find(feature => feature.id === featureId)?.text
}

function getBedbankRoomSizeSqm(pkg: App.BedbankPackage) {
  return getPropertyRoomSizeSqm(getBedbankRoomFeatureText(pkg, 'dimension'))
}

function getBedbankRoomViewText(pkg: App.BedbankPackage) {
  return getBedbankRoomFeatureText(pkg, 'water')
}

function getPropertyRoomTexts(room: App.Property.Room) {
  return [
    room.description,
    ...room.popularAmenities.map(amenity => amenity.name),
    ...room.amenities.flatMap(amenityGroup => amenityGroup.items.map(amenity => amenity.name)),
  ].filter(isTruthyString)
}

function getPackageInclusionDescriptions(pkg: App.HotelPackage) {
  return [
    ...(pkg.inclusions ?? []),
    ...pkg.bonusInclusions,
    ...Object.values(pkg.luxPlusInclusionsByTier ?? {}).flat(),
  ].map(inclusion => inclusion.description)
}

function getHotelPackageRoomTexts(pkg: App.HotelPackage) {
  return [
    pkg.roomType.description,
    ...pkg.roomType.amenities.map(amenity => amenity.description),
  ].filter(isTruthyString)
}

export function buildFlashHotelHighlightTagContext(
  offer: App.HotelOffer,
  property: App.OfferProperty,
  packageViews?: Array<App.OfferPackageOptionView>,
): OfferPageHighlightTagContext {
  const hasPackageViewContext = !!packageViews
  const packages = packageViews?.filter(view => view.hotelAvailable && view.capacityValid).map(view => view.package) ?? offer.packages

  return {
    boardCodes: packages.map(pkg => pkg.boardCode),
    facilities: [
      offer.facilities,
      offer.highlights,
      offer.gettingThere,
      property.address,
      property.geoData.locality,
      property.geoData.administrativeAreaLevel1,
    ].filter(isTruthyString),
    holidayTypes: offer.holidayTypes,
    inclusions: [
      ...(!hasPackageViewContext && offer.inclusions ? [offer.inclusions] : []),
      ...packages.flatMap(getPackageInclusionDescriptions),
    ].filter(isTruthyString),
    rating: property.rating,
    roomAmenities: packages.flatMap(getHotelPackageRoomTexts),
    roomNames: packages.map(pkg => pkg.roomType.name).filter(isTruthyString),
    roomSizeAvailableRoomCount: packages.length,
    roomSizesSqm: packages.map(pkg => pkg.roomType.sizeSqm).filter((size): size is number => typeof size === 'number'),
  }
}

export function buildPropertyHighlightTagContext(
  property: App.Property.Property,
  propertyRates: Array<App.EnrichedPropertyRate>,
): OfferPageHighlightTagContext {
  const rooms = Object.values(property.rooms)
  const availableRoomIds = new Set(propertyRates.map(rate => rate.roomId))
  const roomsWithRates = propertyRates.length > 0 ? rooms.filter(room => availableRoomIds.has(room.id)) : rooms

  return {
    boardCodes: propertyRates.map(rate => rate.boardCode),
    facilities: [
      property.highlights,
      property.locationDescription,
      property.facilities,
      property.location.title,
      property.location.formattedAddress,
      property.location.city,
      property.location.region,
      ...property.popularFacilities.map(facility => facility.name),
      ...property.amenities.flatMap(amenityGroup => amenityGroup.items.map(amenity => amenity.name)),
    ].filter(isTruthyString),
    holidayTypes: property.holidayTypeTags,
    inclusions: roomsWithRates.flatMap(room => [
      ...room.inclusions,
      ...room.bonusInclusions,
      ...room.luxPlusInclusions,
    ]).map(inclusion => inclusion.description),
    locationTags: property.locationTags,
    rating: property.rating,
    roomAmenities: roomsWithRates.flatMap(getPropertyRoomTexts),
    roomNames: roomsWithRates.map(room => room.name).filter(isTruthyString),
    roomSizeAvailableRoomCount: roomsWithRates.length,
    roomSizesSqm: roomsWithRates.map(room => getPropertyRoomSizeSqm(room.size)).filter((size): size is number => typeof size === 'number'),
  }
}

export function buildBedbankHighlightTagContext(
  offer: App.BedbankOffer,
  hotelOnlyRates?: Array<App.BedbankRate>,
): OfferPageHighlightTagContext {
  const hasRateContext = !!hotelOnlyRates
  const packages = hotelOnlyRates?.map(rate => rate.room) ?? offer.packages

  return {
    boardCodes: hotelOnlyRates?.map(rate => rate.boardCode),
    facilities: [
      offer.amenities,
      offer.attractions,
      offer.description,
      offer.tagline,
      ...offer.popularFacilities,
      ...offer.facilityGroups.flatMap(facilityGroup => facilityGroup.values.map(facility => facility.name)),
    ].filter(isTruthyString),
    holidayTypes: offer.holidayTypes ?? [],
    inclusions: hasRateContext ? [] : (offer.inclusions ?? []).map(inclusion => inclusion.description),
    rating: offer.property.rating,
    roomAmenities: packages.flatMap(pkg => [
      pkg.description,
      ...pkg.features.map(feature => feature.text),
      ...pkg.facilityGroups.flatMap(facilityGroup => facilityGroup.values.map(facility => facility.name)),
    ]).filter(isTruthyString),
    roomNames: packages.map(pkg => pkg.name).filter(isTruthyString),
    roomSizeAvailableRoomCount: packages.length,
    roomSizesSqm: packages.map(getBedbankRoomSizeSqm).filter((size): size is number => typeof size === 'number'),
    roomViewTags: packages.map(getBedbankRoomViewText).filter(isTruthyString),
  }
}
